/********************************************************************************
 * Copyright (c) 2022 Cedalo GmbH
 *
 * All rights reserved. This program and the accompanying materials
 * are made available under the terms of the Eclipse Public License 2.0 which accompany this distribution.
 * and Eclipse Distribution License v1.0 which accompany this distribution.
 *
 * The Eclipse Public License is available at
 *   https://www.eclipse.org/legal/epl-2.0/
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/
import OpenInNew from '@mui/icons-material/esm/OpenInNew';
import { Checkbox, IconButton } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import formatDistanceToNow from 'date-fns/formatDistanceToNow';
import React, { useState } from 'react';
import { Path } from '../../../helper/Path';
import { runQuery } from '../../app/GraphQLWSClient';
import { makeUseFilterAndSort } from '../../utils/filterAndSort';
import TableSortHeader from '../../utils/TableSortHeader';
import { withScopeRights } from '../../utils/with';
import * as Mutation from './mutations';

const useFilterAndSort = makeUseFilterAndSort(['name', 'type', 'lastModified'], 'name');

const addMachine = async (id, machineId) => {
	const result = await runQuery(Mutation.addMachine, { id, machineId });
	return result.addIntegrationMachine;
};
const removeMachine = async (id, machineId) => {
	const result = await runQuery(Mutation.removeMachine, { id, machineId });
	return result.removeIntegrationMachine;
};
const selectedIntegration = (integrations) => {
	const selection = integrations.filter((integration) => integration.isUsed);
	return selection.length ? selection[0].id : undefined;
};

export const IntegrationSelectTable = withScopeRights(
	({ integrations, appId, rights /*, onShowIntegrationDetails  , type */ }) => {
		const [selected, setSelected] = useState(selectedIntegration(integrations));
		const { values: sortedIntegrations, sort, setSortField } = useFilterAndSort(integrations);
		const canViewFull = rights.includes('account.view_full');

		const onSort = (ev, property) => setSortField(property);
		const toggleSelection = async (id) => {
			try {
				const selection = selected === id ? null : id;
				const result = await (selection ? addMachine(id, appId) : removeMachine(id, appId));
				if (result.success) setSelected(selection);
			} catch (error) {
				console.log(error);
			}
		};

		return (
			<div>
				<TableContainer sx={{ height: '100%' }}>
					<Table stickyHeader size="medium" aria-label="collapsible table">
						<TableSortHeader
							height={48}
							cells={[
								{ id: 'checked', numeric: false, padding: false, label: '', width: '15%' },
								{ id: 'name', numeric: false, padding: false, label: 'Name', width: '50%' },
								{
									id: 'lastModified',
									hideOnSmallDisplays: true,
									numeric: false,
									label: 'LastModified',
									width: '35%'
								}
							]}
							orderBy={sort.field}
							order={sort.direction}
							onRequestSort={onSort}
						/>
						<TableBody>
							{sortedIntegrations.map((integration) => (
								<TableRow
									style={{
										height: '40px',
										cursor: 'pointer'
									}}
									hover
									tabIndex={-1}
									id={`${integration.id}`}
									key={`${integration.id}`}
								>
									<TableCell padding="none">
										<Checkbox
											color="primary"
											checked={integration.id === selected}
											onClick={() => toggleSelection(integration.id)}
										/>
									</TableCell>
									<TableCell
										style={{ fontWeight: 'bold' }}
										padding="none"
										component="th"
										scope="row"
										// onClick={() => onShowIntegrationDetails(integration.id)}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												whiteSpace: 'nowrap',
												overflow: 'hidden',
												textOverflow: 'ellipsis'
											}}
										>
											<div style={{ lineHeight: '29px' }}>{integration.name}</div>

											{canViewFull ? (
												<IconButton
													style={{ padding: '4px', marginRight: '25px' }}
													size="small"
													onClick={() =>
														window.open(Path.integration(integration.id), '_blank')
													}
												>
													<OpenInNew />
												</IconButton>
											) : null}
										</div>
									</TableCell>
									<TableCell padding="none">
										{formatDistanceToNow(new Date(integration.lastModified), {
											addSuffix: true
										})}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</div>
		);
	}
);
